"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Box, Typography, Tabs, Tab, Button, Paper, Stack } from "@mui/material"
import type { INotification } from "@/api/notification-api"
import { X } from "lucide-react"
import ReadNotificationsTable, { type IMessageModel } from "./ReadNotificationsTable"
import { useCookies } from "react-cookie"
import { useUser } from "@/contexts/UserContext"

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`messages-tabpanel-${index}`}
            aria-labelledby={`messages-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    )
}

export default function Messages({
    data,
    onClose,
    markReadIds,
    setMarkReadIds,
}: {
    data: INotification
    onClose: () => void
    markReadIds: string[]
    setMarkReadIds: (ids: string[]) => void
}) {
    const [tabValue, setTabValue] = useState(0)
    const [checkAll, setCheckAll] = useState(false)
    const [selected, setSelected] = useState<IMessageModel[]>([])
    const [read, setRead] = useState<IMessageModel[]>([])
    const [unread, setUnread] = useState<IMessageModel[]>([])
    const { user } = useUser()
    const [cookies, setCookie] = useCookies(["lastSeenId"])

    // Initialize selected items from data
    useEffect(() => {
        setSelected(
            data.data.map((item) => {
                return {
                    ...item,
                    selected: false,
                    expanded: false,
                }
            }),
        )
    }, [data])

    // Filter read and unread notifications based on read status
    useEffect(() => {
        setUnread(selected.filter((item) => !item.read))
        setRead(selected.filter((item) => item.read))
    }, [selected])

    // Handle expanding a notification and marking it as read
    const saveRead = async (item: IMessageModel) => {
        if (!item.read) {
            // Don't mark as read in API yet, just collect the ID
            setMarkReadIds((prev) => [...prev, item.oldId])

            // Update the selected items locally without changing read status
            setSelected((prev) =>
                prev.map((i) => {
                    if (i.oldId === item.oldId) {
                        return { ...i, expanded: true }
                    }
                    // Collapse other items
                    return { ...i, expanded: false }
                }),
            )
        } else {
            // Just toggle expansion for already read items
            setSelected((prev) =>
                prev.map((i) => {
                    if (i.oldId === item.oldId) {
                        return { ...i, expanded: !i.expanded }
                    }
                    // Collapse other items
                    return { ...i, expanded: false }
                }),
            )
        }
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue)
        // Reset checkAll when changing tabs
        setCheckAll(false)
    }

    // Mark selected notifications as read
    const processMarkRead = async () => {
        const selectedItems = selected.filter((item) => item.selected && !item.read)
        if (selectedItems.length === 0) return

        const selectedIds = selectedItems.map((item) => item.oldId)

        // Don't call API to mark as read yet, just collect the IDs
        setMarkReadIds((prev) => [...prev, ...selectedIds])

        // Update selected items locally without changing read status
        setSelected((prev) =>
            prev.map((item) => {
                if (selectedIds.includes(item.oldId)) {
                    return { ...item, selected: false }
                }
                return item
            }),
        )

        // Reset checkAll
        setCheckAll(false)
    }

    const handleSelectAll = () => {
        const newCheckAll = !checkAll
        setSelected(
            selected.map((item) => {
                // Only toggle selection for items in the current tab
                if (tabValue === 0 && !item.read) {
                    return { ...item, selected: newCheckAll }
                } else if (tabValue === 1 && item.read) {
                    return { ...item, selected: newCheckAll }
                }
                return item
            }),
        )
        setCheckAll(newCheckAll)
    }

    const handleItemToggle = (tgItem: IMessageModel) => {
        const updatedItems = selected.map((item) => {
            if (item.id === tgItem.id) {
                return { ...item, selected: !item.selected }
            }
            return item
        })

        setSelected(updatedItems)

        // Update checkAll state based on current tab
        const currentTabItems =
            tabValue === 0 ? updatedItems.filter((item) => !item.read) : updatedItems.filter((item) => item.read)

        if (currentTabItems.length > 0) {
            setCheckAll(currentTabItems.every((item) => item.selected))
        } else {
            setCheckAll(false)
        }
    }

    return (
        <Box sx={{ width: "100%", bgcolor: "#F7F7F7", p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h5" sx={{ mb: 3, color: "#2196f3", fontWeight: "bold" }}>
                    Notifications
                </Typography>
                <Typography variant="body1" color="text.secondary" onClick={onClose} sx={{ cursor: "pointer" }}>
                    <X />
                </Typography>
            </Box>
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            "& .MuiTab-root": { minWidth: 100 },
                            "& .Mui-selected": { color: "#1976d2" },
                            "& .MuiTabs-indicator": { backgroundColor: "#1976d2" },
                        }}
                    >
                        <Tab label="Unread" />
                        <Tab label="Read" />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    <Stack direction="row" justifyContent="space-between" mb={2}>
                        <Box>
                            <Button
                                variant="outlined"
                                sx={{ mr: 1 }}
                                onClick={processMarkRead}
                                disabled={!unread.some((item) => item.selected)}
                            >
                                Mark as read
                            </Button>
                        </Box>
                    </Stack>
                    <ReadNotificationsTable
                        data={unread}
                        onItemClick={saveRead}
                        showCheckbox={true}
                        checkAll={checkAll}
                        onSelectAll={handleSelectAll}
                        onCheckboxChange={handleItemToggle}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <ReadNotificationsTable data={read} onItemClick={saveRead} showCheckbox={false} />
                </TabPanel>
            </Paper>
        </Box>
    )
}